import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import PageTitle from "../components/PageTitle";
import { useNavigate } from "react-router-dom";
import SwipeableRow from "../components/SwipeableRow";
import {
  deleteWeight,
  displayToKg,
  kgToDisplay,
  loadWeights,
  loadWeightUnit,
  saveWeight,
  saveWeightUnit,
  type WeightLog,
  type WeightUnit
} from "../lib/weight";

export default function Weight() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState<WeightLog[]>([]);
  const [unit, setUnit] = useState<WeightUnit>("lbs");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [input, setInput] = useState("");

  const refresh = useCallback(async () => {
    try {
      const [ws, u] = await Promise.all([loadWeights(), loadWeightUnit()]);
      setLogs(ws);
      setUnit(u);
      setError(null);
    } catch (e) {
      setError((e as Error)?.message ?? String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function add(e: FormEvent) {
    e.preventDefault();
    const value = parseFloat(input);
    if (!Number.isFinite(value) || value <= 0) {
      setError("Enter a positive number.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const kg = displayToKg(value, unit);
      await saveWeight(kg);
      setInput("");
      await refresh();
    } catch (e) {
      setError((e as Error)?.message ?? String(e));
    } finally {
      setSaving(false);
    }
  }

  async function changeUnit(newUnit: WeightUnit) {
    setUnit(newUnit);
    try {
      await saveWeightUnit(newUnit);
    } catch (e) {
      setError((e as Error)?.message ?? String(e));
    }
  }

  async function onDelete(id: string) {
    try {
      await deleteWeight(id);
      await refresh();
    } catch (e) {
      setError((e as Error)?.message ?? String(e));
    }
  }

  const latest = logs[0];
  const prev = logs[1];
  const delta = latest && prev ? latest.weight_kg - prev.weight_kg : null;

  const trend = useMemo(() => buildTrend(logs), [logs]);

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/")} className="text-sm text-slate-400">← Back</button>
          <PageTitle>Weight</PageTitle>
        </div>
        <div className="flex bg-slate-900 border border-slate-800 rounded-lg p-0.5">
          {(["lbs", "kg"] as WeightUnit[]).map((u) => (
            <button
              key={u}
              onClick={() => changeUnit(u)}
              className={`px-3 py-1 rounded text-xs font-medium ${
                unit === u ? "bg-slate-800 text-emerald-400" : "text-slate-400"
              }`}
            >
              {u}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-950 border border-red-800 text-red-200 text-sm">{error}</div>
      )}

      {/* Quick log form */}
      <section className="p-4 rounded-2xl bg-slate-900 border border-slate-800">
        <div className="text-xs text-slate-400 uppercase tracking-wide mb-3">Log weight</div>
        <form onSubmit={add} className="flex gap-2">
          <input
            type="number"
            inputMode="decimal"
            step="0.1"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={`weight in ${unit}`}
            className="flex-1 bg-slate-800 border border-slate-700 rounded px-3 py-2 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500"
          />
          <button
            type="submit"
            disabled={saving || !input}
            className="px-4 py-2 rounded bg-emerald-500 text-slate-950 font-semibold disabled:opacity-50"
          >
            Log
          </button>
        </form>
      </section>

      {/* Latest + trend */}
      {!loading && latest && (
        <section className="p-4 rounded-2xl bg-slate-900 border border-slate-800">
          <div className="flex items-baseline justify-between mb-3">
            <div>
              <div className="text-xs text-slate-400 uppercase tracking-wide">Current</div>
              <div className="text-3xl font-bold mt-1 tabular-nums">
                {kgToDisplay(latest.weight_kg, unit).toFixed(1)}
                <span className="text-base font-normal text-slate-500 ml-1">{unit}</span>
              </div>
              <div className="text-xs text-slate-500 mt-1">{formatDate(latest.logged_at)}</div>
            </div>
            {delta !== null && (
              <div
                className={`text-right ${
                  delta < 0 ? "text-emerald-400" : delta > 0 ? "text-rose-400" : "text-slate-400"
                }`}
              >
                <div className="text-xs">vs last entry</div>
                <div className="font-semibold tabular-nums">
                  {delta > 0 ? "+" : ""}
                  {kgToDisplay(delta, unit).toFixed(1)} {unit}
                </div>
              </div>
            )}
          </div>

          {trend && <TrendChart logs={trend} unit={unit} />}
        </section>
      )}

      {/* History */}
      {!loading && (
        <section className="p-4 rounded-2xl bg-slate-900 border border-slate-800">
          <div className="text-xs text-slate-400 uppercase tracking-wide mb-2">History</div>
          {logs.length === 0 ? (
            <div className="text-sm text-slate-500 py-3">No entries yet.</div>
          ) : (
            <ul className="divide-y divide-slate-800/60">
              {logs.map((w) => (
                <li key={w.id}>
                  <SwipeableRow onDelete={() => onDelete(w.id)} bgClass="bg-slate-900">
                    <div className="py-2 flex justify-between items-center px-1">
                      <div>
                        <div className="text-slate-100 tabular-nums">
                          {kgToDisplay(w.weight_kg, unit).toFixed(1)} {unit}
                        </div>
                        <div className="text-xs text-slate-500">{formatDate(w.logged_at)}</div>
                      </div>
                    </div>
                  </SwipeableRow>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}

function buildTrend(logs: WeightLog[]): WeightLog[] | null {
  if (logs.length < 2) return null;
  // Use chronological order (oldest first) for the chart.
  return [...logs].slice(0, 30).reverse();
}

function TrendChart({ logs, unit }: { logs: WeightLog[]; unit: WeightUnit }) {
  const values = logs.map((l) => kgToDisplay(l.weight_kg, unit));
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = Math.max(max - min, 1);
  const padded = { min: min - range * 0.1, max: max + range * 0.1 };
  const span = padded.max - padded.min;

  const w = 320;
  const h = 96;

  const points = values.map((v, i) => {
    const x = (i / Math.max(values.length - 1, 1)) * w;
    const y = h - ((v - padded.min) / span) * h;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });

  return (
    <div className="mt-2">
      <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full h-24">
        <polyline
          fill="none"
          stroke="#34d399"
          strokeWidth="2"
          points={points.join(" ")}
        />
        {points.map((p, i) => {
          const [x, y] = p.split(",").map(Number);
          return <circle key={i} cx={x} cy={y} r={2} fill="#34d399" />;
        })}
      </svg>
      <div className="flex justify-between text-[10px] text-slate-400 mt-1">
        <span>{padded.min.toFixed(1)}</span>
        <span>{padded.max.toFixed(1)} {unit}</span>
      </div>
    </div>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}
